import { fnv1a64 } from './similarity'
import { canonicalSemanticJson } from './semanticGit'
import { corpusEvidenceRecordId, evidenceBindingId, webEvidenceRecordId } from './evidenceIds'
import type { Session } from './types'
import { assessmentBindingIssue, type AssessmentAgendaBinding } from './assessmentAgenda'
import {
  diagnosticPlanIssue,
  diagnosticPriority,
  mathHead,
  mathematicalDependencyIssue,
  sameMathRef,
  type MathRef,
} from './mathematics'
import { mathematicalContextFor } from './mathSession'

export const AGENDA_VERSION = 1 as const
export const MAX_AGENDA_STEPS = 64
export const AGENDA_BRANCH_STREAK = 2
export const AGENDA_ACTIONS = [
  'verify',
  'deepen',
  'challenge',
  'experiment',
  'expand',
  'diverge',
  'invert',
] as const
export type AgendaAction = (typeof AGENDA_ACTIONS)[number]
export type AgendaPriority =
  'focus' | 'changed-evidence' | 'uncertainty' | 'coverage-gap' | 'alternative'
export type AgendaStatus = 'pending' | 'running' | 'completed' | 'failed' | 'unknown' | 'dismissed'
export interface AgendaRevision {
  claim: string
  evidence: string
  scope: string
  premises: string
  brief: string
}
export interface AgendaAttempt {
  id: string
  startedAt: number
  finishedAt: number | null
  opId: string | null
  status: 'running' | 'completed' | 'failed' | 'unknown'
}
export interface AgendaStep {
  id: string
  purpose: string
  nodeId: string
  action: AgendaAction
  priority: AgendaPriority
  revision: AgendaRevision
  dependsOn: string[]
  createdAt: number
  status: AgendaStatus
  attempts: AgendaAttempt[]
  mathematics?: { target: MathRef; requires: MathRef[]; plan: MathRef | null }
  assessment?: AssessmentAgendaBinding
}
export interface InvestigationAgenda {
  version: typeof AGENDA_VERSION
  steps: AgendaStep[]
}

const priorities: Record<AgendaPriority, number> = {
  focus: 0,
  'changed-evidence': 1,
  uncertainty: 2,
  'coverage-gap': 3,
  alternative: 4,
}
export const AGENDA_PRIORITY_REASON: Record<AgendaPriority, string> = {
  focus: 'Explicit user focus comes first.',
  'changed-evidence': 'Changed evidence is addressed before unresolved uncertainty.',
  uncertainty: 'A consequential uncertainty is addressed before a coverage gap.',
  'coverage-gap': 'An explicit coverage gap is addressed before alternatives.',
  alternative: 'Explore an alternative after higher-priority queued work.',
}
const hash = (value: unknown) => fnv1a64(canonicalSemanticJson(value))
const object = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === 'object' && !Array.isArray(v)
const text = (v: unknown, max: number): v is string =>
  typeof v === 'string' && v.length > 0 && v.length <= max
const time = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v) && v >= 0

/** Freeze the inputs that can change this action's meaning, including every
 * retained local premise. Hashes detect changes, not authenticity or truth. */
export function agendaRevisionFor(session: Session, nodeId: string): AgendaRevision | null {
  const node = session.nodes[nodeId]
  if (!node) return null
  const research = session.research
  const retainedInputs = (id: string) => {
    const n = session.nodes[id]
    if (!n) return { id, missing: true }
    const bindings = research?.manualEvidenceBindings.filter((b) => b.nodeId === id) ?? []
    const records = new Set([
      ...bindings.map((b) => b.recordId),
      ...(n.verification?.sources ?? []).map((source) => webEvidenceRecordId(source.url)),
      ...(n.verification?.corpus?.cites ?? []).map((cite) => corpusEvidenceRecordId(cite.docId)),
    ])
    const bindingIds = new Set(bindings.map((b) => b.id))
    if (n.verification)
      for (const recordId of records)
        bindingIds.add(evidenceBindingId(id, n.verification, recordId))
    return {
      id,
      claim: n.claim,
      parentId: n.parentId,
      bridgeFrom: n.bridgeFrom ?? [],
      reasoning: n.reasoning,
      evidence: n.evidence,
      elaboration: n.elaboration,
      foreignFrom: n.foreignFrom,
      model: n.model,
      verification: n.verification ?? null,
      outcome: n.outcome ?? null,
      verificationHistory: n.verificationHistory ?? [],
      evidenceRebases: n.evidenceRebases ?? [],
      bindings,
      records: research?.evidenceRecords.filter((record) => records.has(record.id)) ?? [],
      annotations:
        research?.evidenceAnnotations.filter((row) =>
          row.key.kind === 'binding' ? bindingIds.has(row.key.id) : records.has(row.key.id),
        ) ?? [],
      scopes: research?.claimScopes.filter((row) => row.nodeId === id) ?? [],
      claimAnnotations: research?.claimAnnotations.filter((row) => row.nodeId === id) ?? [],
    }
  }
  const seen = new Set<string>([nodeId])
  const pending = [...(node.parentId ? [node.parentId] : []), ...(node.bridgeFrom ?? [])]
  const premises: unknown[] = []
  while (pending.length) {
    const id = pending.pop()!
    if (seen.has(id)) continue
    seen.add(id)
    const n = session.nodes[id]
    premises.push(retainedInputs(id))
    if (n?.parentId) pending.push(n.parentId)
    if (n?.bridgeFrom) pending.push(...n.bridgeFrom)
  }
  return {
    claim: node.claim,
    evidence: hash(retainedInputs(nodeId)),
    scope: hash(session.research?.claimScopes.filter((row) => row.nodeId === nodeId) ?? []),
    premises: hash(premises),
    brief: hash({
      seed: session.seed,
      frame: session.frame ?? 'claim',
      brief: session.brief ?? [],
      ...(research?.mathematics?.enabled
        ? {
            mathematics: research.mathematics.obligations
              .filter((o) => o.nodeId === nodeId)
              .map((o) => ({
                id: o.id,
                revision: o.revision,
                package: mathHead(research.mathematics!.packages, o.package.id)?.revision,
              })),
          }
        : {}),
    }),
  }
}

/** Reject an entire malformed queue, never drop an attempt and leave its step
 * eligible to spend again. This optional extension leaves legacy hashes alone. */
export function parseAgenda(value: unknown): InvestigationAgenda | null {
  if (
    !object(value) ||
    value.version !== AGENDA_VERSION ||
    !Array.isArray(value.steps) ||
    value.steps.length > MAX_AGENDA_STEPS
  )
    return null
  const ids = new Set<string>()
  const attemptIds = new Set<string>()
  const steps: AgendaStep[] = []
  for (const row of value.steps) {
    if (
      !object(row) ||
      !text(row.id, 200) ||
      ids.has(row.id) ||
      !text(row.nodeId, 200) ||
      !text(row.purpose, 2000) ||
      !AGENDA_ACTIONS.includes(row.action as AgendaAction) ||
      !Object.hasOwn(priorities, row.priority as string) ||
      !time(row.createdAt) ||
      !['pending', 'running', 'completed', 'failed', 'unknown', 'dismissed'].includes(
        row.status as string,
      ) ||
      !object(row.revision) ||
      !Array.isArray(row.dependsOn) ||
      row.dependsOn.length > MAX_AGENDA_STEPS ||
      !row.dependsOn.every((id) => text(id, 200)) ||
      !Array.isArray(row.attempts) ||
      row.attempts.length > 1
    )
      return null
    if (
      !['claim', 'evidence', 'scope', 'premises', 'brief'].every((key) =>
        text(
          row.revision && (row.revision as Record<string, unknown>)[key],
          key === 'claim' ? 20000 : 100,
        ),
      )
    )
      return null
    const attempts: AgendaAttempt[] = []
    if (
      row.assessment !== undefined &&
      (!object(row.assessment) ||
        !['assessmentId', 'actionId', 'packetRevision'].every((key) =>
          text((row.assessment as Record<string, unknown>)[key], 240),
        ))
    )
      return null
    if (row.mathematics !== undefined) {
      const m = row.mathematics
      const reference = (r: unknown) => object(r) && text(r.id, 240) && text(r.revision, 100)
      if (
        !object(m) ||
        !reference(m.target) ||
        !Array.isArray(m.requires) ||
        m.requires.length > 512 ||
        !m.requires.every(reference) ||
        (m.plan !== null && !reference(m.plan))
      )
        return null
    }
    for (const attempt of row.attempts) {
      if (
        !object(attempt) ||
        !text(attempt.id, 200) ||
        attemptIds.has(attempt.id) ||
        !time(attempt.startedAt) ||
        (attempt.finishedAt !== null &&
          (!time(attempt.finishedAt) || attempt.finishedAt < attempt.startedAt)) ||
        (attempt.opId !== null && !text(attempt.opId, 200)) ||
        !['running', 'completed', 'failed', 'unknown'].includes(attempt.status as string)
      )
        return null
      if (
        (attempt.status === 'completed' || attempt.status === 'failed') &&
        attempt.finishedAt === null
      )
        return null
      attemptIds.add(attempt.id)
      attempts.push({
        id: attempt.id,
        startedAt: attempt.startedAt,
        finishedAt: attempt.finishedAt as number | null,
        opId: attempt.opId as string | null,
        status: attempt.status as AgendaAttempt['status'],
      })
    }
    if (
      (row.status === 'pending' && attempts.length) ||
      (['running', 'completed', 'failed', 'unknown'].includes(row.status as string) &&
        attempts.length !== 1)
    )
      return null
    if (attempts.length && row.status !== 'dismissed' && attempts[0]!.status !== row.status)
      return null
    ids.add(row.id)
    steps.push({
      id: row.id,
      nodeId: row.nodeId,
      purpose: row.purpose,
      action: row.action as AgendaAction,
      ...(row.assessment
        ? { assessment: structuredClone(row.assessment) as unknown as AssessmentAgendaBinding }
        : {}),
      ...(row.mathematics
        ? { mathematics: structuredClone(row.mathematics) as AgendaStep['mathematics'] }
        : {}),
      priority: row.priority as AgendaPriority,
      revision: {
        claim: row.revision.claim as string,
        evidence: row.revision.evidence as string,
        scope: row.revision.scope as string,
        premises: row.revision.premises as string,
        brief: row.revision.brief as string,
      },
      dependsOn: [...new Set(row.dependsOn as string[])],
      createdAt: row.createdAt,
      status: row.status as AgendaStatus,
      attempts,
    })
  }
  return { version: AGENDA_VERSION, steps }
}

export function agendaStepIssue(
  session: Session,
  agenda: InvestigationAgenda,
  step: AgendaStep,
): string | null {
  if (step.assessment) {
    const issue = assessmentBindingIssue(session, step.assessment)
    if (issue) return issue
  }
  if (session.research?.mathematicsInvalid)
    return 'The mathematical workspace requires repair before execution.'
  const mathematics = session.research?.mathematics
  if (mathematics?.enabled || step.mathematics) {
    if (!mathematics || !step.mathematics)
      return 'Bind this action to an exact mathematical obligation before running it.'
    const binding = step.mathematics
    const target = mathHead(mathematics.obligations, binding.target.id)
    if (
      !target ||
      !sameMathRef(target, binding.target) ||
      target.nodeId !== step.nodeId ||
      !sameMathRef(mathHead(mathematics.packages, target.package.id), target.package)
    )
      return 'The mathematical target or its canonical package changed.'
    const context = mathematicalContextFor(session)
    const issue = mathematicalDependencyIssue(
      mathematics,
      [...target.requires, ...binding.requires],
      context,
    )
    if (issue) return issue
    if (step.action === 'experiment' && !binding.plan)
      return 'An experiment needs a diagnostic outcome plan.'
    if (binding.plan) {
      const plan = mathHead(mathematics.plans, binding.plan.id)
      if (!plan || !sameMathRef(plan, binding.plan) || !sameMathRef(plan.target, target))
        return 'The diagnostic experiment plan changed or belongs to another target.'
      const issue = diagnosticPlanIssue(mathematics, plan, context)
      if (issue) return issue
    }
  }
  if (step.status !== 'pending' || step.attempts.length)
    return 'This step was already attempted or dismissed; it will not run again.'
  if (
    agenda.steps.some((row) =>
      row.attempts.some((a) => a.status === 'running' || a.status === 'unknown'),
    )
  )
    return 'An earlier attempt is running or has an unknown outcome. Reconcile its receipt before continuing.'
  const revision = agendaRevisionFor(session, step.nodeId)
  if (!revision) return 'The target claim is no longer available.'
  if (hash(revision) !== hash(step.revision))
    return 'The claim, evidence, scope, premises or brief changed. Inspect the change and queue a new step.'
  if (step.action === 'verify' && session.nodes[step.nodeId]?.parentId === null)
    return 'Source checks apply to derived claims, not the starting question.'
  if (
    step.dependsOn.some((id) => agenda.steps.find((row) => row.id === id)?.status !== 'completed')
  )
    return 'A prerequisite step has not completed.'
  return null
}

export function queueAgendaStep(
  session: Session,
  input: {
    id: string
    nodeId: string
    purpose: string
    action: AgendaAction
    priority: AgendaPriority
    dependsOn?: string[]
    mathematics?: AgendaStep['mathematics']
    assessment?: AssessmentAgendaBinding
    at: number
  },
): InvestigationAgenda {
  const held = session.research?.agenda ?? { version: AGENDA_VERSION, steps: [] }
  const revision = agendaRevisionFor(session, input.nodeId)
  if (!revision) throw new Error('The target claim is no longer available.')
  const math = session.research?.mathematics
  const target = math?.obligations.findLast((o) => o.nodeId === input.nodeId)
  const mathematics =
    input.mathematics ??
    (math?.enabled && target
      ? { target: { id: target.id, revision: target.revision }, requires: [], plan: null }
      : undefined)
  if (math?.enabled && !mathematics)
    throw new Error('Create and select a mathematical obligation for this target first.')
  if (
    held.steps.some(
      (step) =>
        step.nodeId === input.nodeId &&
        step.action === input.action &&
        hash(step.mathematics ?? null) === hash(mathematics ?? null) &&
        hash(step.revision) === hash(revision),
    )
  )
    throw new Error(
      'This action on these inputs is already in the agenda; inspect its existing attempt.',
    )
  if (held.steps.length >= MAX_AGENDA_STEPS)
    throw new Error(
      `The agenda retains at most ${MAX_AGENDA_STEPS} steps. Export this investigation before starting a new one.`,
    )
  const next = parseAgenda({
    version: AGENDA_VERSION,
    steps: [
      ...held.steps,
      {
        id: input.id,
        nodeId: input.nodeId,
        purpose: input.purpose,
        action: input.action,
        priority: input.priority,
        revision,
        dependsOn: input.dependsOn ?? [],
        ...(mathematics ? { mathematics } : {}),
        ...(input.assessment ? { assessment: input.assessment } : {}),
        createdAt: input.at,
        status: 'pending',
        attempts: [],
      },
    ],
  })
  if (!next) throw new Error('The agenda step is malformed.')
  return next
}

export function pickAgendaStep(
  session: Session,
  agenda: InvestigationAgenda,
): { step: AgendaStep; rule: string } | null {
  const ready = agenda.steps
    .filter((step) => !agendaStepIssue(session, agenda, step))
    .sort(
      (a, b) =>
        priorities[a.priority] - priorities[b.priority] ||
        mathematicalAgendaPriority(session, a) - mathematicalAgendaPriority(session, b) ||
        a.createdAt - b.createdAt ||
        a.id.localeCompare(b.id),
    )
  const first = ready[0]
  if (!first) return null
  const latest = agenda.steps
    .flatMap((step) =>
      step.attempts.map((attempt) => ({ nodeId: step.nodeId, at: attempt.startedAt })),
    )
    .sort((a, b) => b.at - a.at)
    .slice(0, AGENDA_BRANCH_STREAK)
  const repeated =
    latest.length === AGENDA_BRANCH_STREAK && latest.every((row) => row.nodeId === first.nodeId)
  const alternate = repeated
    ? ready.find(
        (step) =>
          step.priority === first.priority &&
          mathematicalAgendaPriority(session, step) ===
            mathematicalAgendaPriority(session, first) &&
          step.nodeId !== first.nodeId,
      )
    : undefined
  return {
    step: alternate ?? first,
    rule: `${AGENDA_PRIORITY_REASON[first.priority]} ${first.mathematics ? 'Within this priority, target tests precede repair regressions, implementation validation, generic controls and exploration. ' : ''}Oldest eligible step breaks ties.${alternate ? ` After ${AGENDA_BRANCH_STREAK} consecutive attempts on one target, another target at the same priority goes first.` : ''}`,
  }
}

function mathematicalAgendaPriority(session: Session, step: AgendaStep): number {
  const plan = step.mathematics?.plan
  const state = session.research?.mathematics
  if (!plan || !state) return 2
  const row = mathHead(state.plans, plan.id)
  return row ? diagnosticPriority(row.role) : 5
}

export function beginAgendaAttempt(
  session: Session,
  stepId: string,
  attemptId: string,
  at: number,
): InvestigationAgenda {
  const agenda = session.research?.agenda
  const step = agenda?.steps.find((row) => row.id === stepId)
  if (!agenda || !step) throw new Error('The agenda step is no longer available.')
  const issue = agendaStepIssue(session, agenda, step)
  if (issue) throw new Error(issue)
  const next = parseAgenda({
    version: AGENDA_VERSION,
    steps: agenda.steps.map((row) =>
      row.id !== stepId
        ? row
        : {
            ...row,
            status: 'running',
            attempts: [
              { id: attemptId, startedAt: at, finishedAt: null, opId: null, status: 'running' },
            ],
          },
    ),
  })
  if (!next) throw new Error('The attempt is malformed; the agenda remains unattempted.')
  return next
}

export function patchAgendaAttempt(
  agenda: InvestigationAgenda,
  stepId: string,
  attemptId: string,
  patch: Partial<Pick<AgendaAttempt, 'opId' | 'finishedAt' | 'status'>>,
): InvestigationAgenda {
  const next = {
    version: AGENDA_VERSION,
    steps: agenda.steps.map((step) =>
      step.id !== stepId
        ? step
        : {
            ...step,
            status: patch.status ?? step.status,
            attempts: step.attempts.map((attempt) =>
              attempt.id !== attemptId ? attempt : { ...attempt, ...patch },
            ),
          },
    ),
  }
  const parsed = parseAgenda(next)
  if (!parsed)
    throw new Error('The attempt update is inconsistent; its previous state is preserved.')
  return parsed
}

/** A read-only receipt join can recover a finished invocation after a crash.
 * Missing/truncated usage never becomes a free replay or a successful finding. */
export function reconcileAgendaAttempts(session: Session): InvestigationAgenda | null {
  const agenda = session.research?.agenda
  if (!agenda) return null
  let next = agenda
  for (const step of agenda.steps) {
    const attempt = step.attempts[0]
    if (!attempt || !['running', 'unknown'].includes(attempt.status)) continue
    // One retained invocation can settle exactly one recorded attempt. A copied
    // operation id is not another receipt, even when both steps target one claim.
    if (
      agenda.steps.filter((row) => row.attempts.some((other) => other.opId === attempt.opId))
        .length !== 1
    )
      continue
    const op = session.ops.find((row) => row.id === attempt.opId)
    if (
      !op?.finishedAt ||
      !time(op.startedAt) ||
      !time(op.finishedAt) ||
      op.startedAt < attempt.startedAt ||
      op.finishedAt < op.startedAt ||
      op.kind !== step.action ||
      session.ops.filter((row) => row.id === attempt.opId).length !== 1 ||
      op.targetIds.length !== 1 ||
      !op.targetIds.includes(step.nodeId) ||
      !op.modelCalls?.length
    )
      continue
    const callIds = new Set<string>()
    if (
      !op.modelCalls.every((call, index) => {
        const unique = !callIds.has(call.id)
        callIds.add(call.id)
        return (
          unique &&
          call.ordinal === index + 1 &&
          time(call.startedAt) &&
          time(call.completedAt) &&
          call.startedAt >= (index ? op.modelCalls![index - 1]!.completedAt : op.startedAt) &&
          call.completedAt >= call.startedAt &&
          call.completedAt <= op.finishedAt!
        )
      })
    )
      continue
    const known = op.modelCalls.every(
      (call) =>
        call.usage &&
        [call.usage.inputTokens, call.usage.outputTokens, call.usage.searches].every(
          (n) => Number.isFinite(n) && n >= 0,
        ) &&
        !(
          call.outcome.kind === 'failed' &&
          call.usage.inputTokens === 0 &&
          call.usage.outputTokens === 0
        ),
    )
    if (!known) continue
    const complete =
      !op.error &&
      !op.refused &&
      op.modelCalls.every(
        (call) => call.outcome.kind === 'completed' && call.outcome.truncated === false,
      )
    next = patchAgendaAttempt(next, step.id, attempt.id, {
      status: complete ? 'completed' : 'failed',
      finishedAt: op.finishedAt,
    })
  }
  return next
}
